// Shared map data for the city: street grid, ground heights, places, the guided
// route and collision. Used by rendering, movement and tests alike.
export type Point = { x: number; z: number };

// Street grid from the Downtown City MegaKit: crossroads are 24.67 m square and
// 4-lane streets come in 6 m slices (18 m wide including both pavements).
export const CITY = (() => {
  const HALF = 12.335, SLICE = 6, SLICES = 8;
  const L = HALF * 2 + SLICE * SLICES; // crossroads-to-crossroads spacing (72.67 m)
  const lines = [-1.5 * L, -0.5 * L, 0.5 * L, 1.5 * L]; // street centre lines (x and z)
  return {
    HALF, SLICE, SLICES, L, lines,
    ROAD: 6, // road surface half-width; pavement runs 6–9 m from the centre line
    KERB: 9, // building line
    EDGE: 1.5 * L + 8.5, // walkable limit (outer pavement)
  };
})();
// Kept for code that still refers to the old terrain settings.
export const TERRAIN = { size: CITY.EDGE * 2 + 120, segments: 1, centerZ: 0 };

export const locations = {
  downtown: {
    name: "Downtown",
    x: CITY.L,
    z: CITY.L / 2 + 8.2,
    description: "Busy streets, brick towers and somewhere to be.",
  },
  plaza: {
    name: "Meeting square",
    x: 0,
    z: 0,
    description: "The waiting room. Sit, wave, and hang about until it's time.",
  },
  park: {
    name: "The park",
    x: -CITY.L,
    z: 0,
    description: "A lawn between the towers. Somewhere to stretch your legs.",
  },
} as const;
export type LocationId = keyof typeof locations;

/** Ground is flat; roads sit one kerb (15 cm) below the pavements. */
export function onRoad(x: number, z: number) {
  const inside = Math.abs(x) < CITY.EDGE + 3 && Math.abs(z) < CITY.EDGE + 3;
  if (!inside) return false;
  return CITY.lines.some((s) => Math.abs(x - s) < CITY.ROAD || Math.abs(z - s) < CITY.ROAD);
}
export function terrainHeight(x: number, z: number): number {
  return onRoad(x, z) ? -0.15 : 0;
}
export const groundHeight = terrainHeight;

/** Feet height. The square and the park are flat ground; roads sit one kerb down. */
export function surfaceHeight(x: number, z: number) {
  return groundHeight(x, z);
}

export function seeded(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Guided route from Downtown to the meeting square: west along the pavement,
// over the zebra crossings, then in through the square's east side to the middle.
// Pedestrians walk 8.2 m from a street's centre line: on the pavement, between
// the kerbside trees (6–7.8 m) and the building fronts (9 m).
export const WALK = 8.2;
const inside = CITY.L / 2 - WALK; // plaza-side pavement
export const road: Point[] = [
  { x: locations.downtown.x, z: locations.downtown.z },
  { x: inside, z: CITY.L / 2 + WALK },
  { x: inside, z: 0 },
  { x: locations.plaza.x, z: locations.plaza.z },
];

export function segmentDistance(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x,
    dz = b.z - a.z,
    l = dx * dx + dz * dz;
  const t = l
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / l))
    : 0;
  return Math.hypot(p.x - a.x - t * dx, p.z - a.z - t * dz);
}
export function roadDistance(x: number, z: number) {
  let d = Infinity;
  for (let i = 1; i < road.length; i++)
    d = Math.min(d, segmentDistance({ x, z }, road[i - 1], road[i]));
  return d;
}

export interface CircleObstacle {
  x: number;
  z: number;
  r: number;
}
/** Axis-aligned rectangle (buildings), half-width hw on x and hd on z. */
export interface BoxObstacle {
  x: number;
  z: number;
  hw: number;
  hd: number;
}
export type Obstacle = CircleObstacle | BoxObstacle;

export function moveWithCollisions(
  p: Point,
  dx: number,
  dz: number,
  obstacles: Obstacle[],
  radius = 0.35,
): Point {
  let x = Math.max(-CITY.EDGE, Math.min(CITY.EDGE, p.x + dx)),
    z = Math.max(-CITY.EDGE, Math.min(CITY.EDGE, p.z + dz));
  for (const o of obstacles) {
    if ("r" in o) {
      const d = Math.hypot(x - o.x, z - o.z),
        r = o.r + radius;
      if (d < r) {
        const a = d > 0.0001 ? Math.atan2(z - o.z, x - o.x) : 0;
        x = o.x + Math.cos(a) * r;
        z = o.z + Math.sin(a) * r;
      }
    } else {
      const lx = x - o.x, lz = z - o.z;
      if (Math.abs(lx) >= o.hw + radius || Math.abs(lz) >= o.hd + radius) continue;
      const cx = Math.max(-o.hw, Math.min(o.hw, lx)), cz = Math.max(-o.hd, Math.min(o.hd, lz));
      const ex = lx - cx, ez = lz - cz, d = Math.hypot(ex, ez);
      if (d > 0.0001) {
        if (d < radius) {
          x = o.x + cx + (ex / d) * radius;
          z = o.z + cz + (ez / d) * radius;
        }
      } else {
        // Centre inside the box: leave by the nearest side.
        const px = o.hw + radius - Math.abs(lx), pz = o.hd + radius - Math.abs(lz);
        if (px < pz) x = o.x + Math.sign(lx || 1) * (o.hw + radius);
        else z = o.z + Math.sign(lz || 1) * (o.hd + radius);
      }
    }
  }
  return { x, z };
}
